import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";

const STATUS_LABEL = { completo: "Completo", parcial: "Parcial", sin_datos_contrato: "Sin franjas cargadas" };
const STATUS_BADGE = { completo: "green", parcial: "amber", sin_datos_contrato: "gray" };

export function ScheduleCoverageView({ clientId, siteId, month, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientId) { setData(null); return; }
    setLoading(true);
    api.get("/schedule/coverage", { params: { client_id: clientId, site_id: siteId || undefined, month } })
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [clientId, siteId, month, refreshKey]);

  if (!clientId) {
    return <div className="card" style={{ padding: 16, fontSize: 12, color: "var(--invict-text-muted)" }} data-testid="schedule-coverage-empty">Elegí un objetivo en el filtro para ver el análisis de cobertura.</div>;
  }
  if (loading || !data) return <div className="skel" style={{ height: 160 }} />;

  const daysWithActivity = data.days.filter((d) => d.status !== "sin_datos_contrato" || d.asignados.length || d.francos.length);

  return (
    <div data-testid="schedule-coverage" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div className="card" style={{ padding: 16 }} data-testid="coverage-days-table">
        <h3 style={{ margin: "0 0 10px", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 800, color: "#0A0A0A" }}>Cobertura por día ({data.total_turnos_mes} turnos este mes)</h3>
        {daysWithActivity.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Sin franjas contratadas ni turnos cargados este mes.</div>}
        <div style={{ maxHeight: 320, overflowY: "auto" }}>
          {daysWithActivity.map((d) => (
            <div key={d.date} style={{ padding: "8px 0", borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`coverage-day-${d.date}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, fontWeight: 800 }}>{d.date}</span>
                <span className={`badge ${STATUS_BADGE[d.status]}`} data-testid={`coverage-status-${d.date}`}>{STATUS_LABEL[d.status]}</span>
                {d.faltan > 0 && <span style={{ fontSize: 11, color: "var(--invict-red)", fontWeight: 700 }}>Faltan {d.faltan}</span>}
                {d.francos.length > 0 && <span className="badge gray">Franco: {d.francos.join(", ")}</span>}
              </div>
              {d.asignados.length > 0 && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 2 }}>Asignados: {d.asignados.join(", ")}</div>}
              {d.shifts?.some((s) => s.faltan > 0) && (
                <div style={{ fontSize: 11, color: "var(--invict-red)", marginTop: 2 }}>
                  {d.shifts.filter((s) => s.faltan > 0).map((s) => `${s.label} ${s.time_from}-${s.time_to}: faltan ${s.faltan} (asignados: ${s.quienes.join(", ") || "ninguno"})`).join(" · ")}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }} className="max-md:!grid-cols-1">
        <div className="card" style={{ padding: 16 }} data-testid="coverage-monthly-summary">
          <h3 style={{ margin: "0 0 10px", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 800, color: "#0A0A0A" }}>Resumen mensual por persona</h3>
          {data.monthly_summary.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Sin turnos cargados.</div>}
          {data.monthly_summary.map((p) => (
            <div key={p.user_id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--invict-bone-3)", fontSize: 12 }} data-testid={`coverage-summary-person-${p.user_id}`}>
              <span>{p.user_name}</span>
              <span>{p.turnos} turnos · {p.dias_asignados} días · <b>{p.total_hours} h</b></span>
            </div>
          ))}
        </div>
        <div className="card" style={{ padding: 16 }} data-testid="coverage-weekly-load">
          <h3 style={{ margin: "0 0 10px", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 800, color: "#0A0A0A" }}>Carga semanal (detección de sobrecarga)</h3>
          {data.weekly_load.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Sin turnos cargados.</div>}
          {data.weekly_load.map((w) => (
            <div key={`${w.user_id}-${w.week_start}`} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--invict-bone-3)", fontSize: 12 }} data-testid={`coverage-weekly-${w.user_id}-${w.week_start}`}>
              <span>{w.user_name} · sem. {w.week_start}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <b style={{ color: w.overloaded ? "var(--invict-red)" : "inherit" }}>{w.total_hours} h</b>
                {w.overloaded && <span className="badge red" style={{ fontSize: 9 }}>Sobrecarga</span>}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
